import { FC, useCallback, useState } from 'react';
import { Button, Divider, Form, Input, Modal, Row, Select, Space, Switch, Table, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { NAMESPACE, DELETE_ACTION_COLOR } from '@/constants';
import {
  useGetAdminChannelsQuery,
  useCreateAdminChannelMutation,
  useUpdateAdminChannelMutation,
  useDeleteAdminChannelMutation,
  useSeedAdminChannelsFromRateCardsMutation,
  useGetDspOptionsQuery,
  AdminChannel
} from '@/api';

/** Same limit as the line item form's targeting details field. */
const TARGETING_DETAILS_MAX_LENGTH = 300;

type ChannelFormValues = {
  channel: string;
  isActive?: boolean;
  defaultTargetingDetails?: string;
  defaultDsp?: string;
};

const { confirm } = Modal;

const normalizeChannelKey = (channel: string) => channel.trim().toUpperCase();

const TraderControlsChannels: FC = () => {
  const { t } = useTranslation(NAMESPACE.COMMON);
  const [form] = Form.useForm<ChannelFormValues>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState<AdminChannel | null>(null);

  const { data, isLoading } = useGetAdminChannelsQuery({ includeInactive: true });
  const [createChannel, { isLoading: isCreating }] = useCreateAdminChannelMutation();
  const [updateChannel, { isLoading: isUpdating }] = useUpdateAdminChannelMutation();
  const [deleteChannel, { isLoading: isDeleting }] = useDeleteAdminChannelMutation();
  const [seedFromRateCards, { isLoading: isSeeding }] = useSeedAdminChannelsFromRateCardsMutation();
  const { data: dspOptionsData, isLoading: areDspsLoading } = useGetDspOptionsQuery();

  const openAddModal = useCallback(() => {
    setEditingChannel(null);
    form.resetFields();
    setModalOpen(true);
  }, [form]);

  const openEditModal = useCallback(
    (record: AdminChannel) => {
      setEditingChannel(record);
      form.setFieldsValue({
        channel: record.label,
        isActive: record.isActive,
        defaultTargetingDetails: record.defaultTargetingDetails,
        defaultDsp: record.defaultDsp
      });
      setModalOpen(true);
    },
    [form]
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingChannel(null);
    form.resetFields();
  }, [form]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const channel = values.channel.trim();
      /* Always sent, blank included: a blank value clears the default on the server. */
      const defaults = {
        defaultTargetingDetails: values.defaultTargetingDetails?.trim() ?? '',
        defaultDsp: values.defaultDsp ?? ''
      };
      if (editingChannel) {
        await updateChannel({
          id: editingChannel._id,
          body: {
            label: channel,
            ...defaults,
            ...(values.isActive !== undefined && { isActive: values.isActive })
          }
        }).unwrap();
        message.success(t('channelHasBeenEdited'));
      } else {
        await createChannel({ key: normalizeChannelKey(channel), label: channel, ...defaults }).unwrap();
        message.success(t('channelHasBeenCreated'));
      }
      closeModal();
    } catch (err) {
      if (err && typeof err === 'object' && 'data' in err) {
        message.error((err as { data?: { message?: string } }).data?.message ?? 'Something went wrong');
      }
    }
  }, [editingChannel, form, createChannel, updateChannel, closeModal, t]);

  const handleDelete = useCallback(
    (record: AdminChannel) => {
      const id = record._id;
      confirm({
        title: <span style={{ color: DELETE_ACTION_COLOR }}>{t('deleteChannelModalTitle')}</span>,
        content: t('deleteChannelModalDescription'),
        icon: <ExclamationCircleOutlined />,
        okText: t('cancel'),
        cancelText: t('delete'),
        cancelButtonProps: { danger: true, style: { color: DELETE_ACTION_COLOR } },
        onCancel: () =>
          deleteChannel(id)
            .unwrap()
            .then(() => message.success(t('channelHasBeenDeleted')))
      });
    },
    [deleteChannel, t]
  );

  const handleSeedFromRateCards = useCallback(() => {
    seedFromRateCards()
      .unwrap()
      .then(() => message.success(t('channelsSeededFromRateCards')))
      .catch(() => message.error(t('channelsSeedFailed')));
  }, [seedFromRateCards, t]);

  const columns: ColumnsType<AdminChannel> = [
    {
      title: t('channel'),
      dataIndex: 'label',
      key: 'label',
      sorter: (a, b) => (a.label ?? '').localeCompare(b.label ?? ''),
      render: (val) => val ?? '—'
    },
    {
      title: t('defaultDsp'),
      dataIndex: 'defaultDsp',
      key: 'defaultDsp',
      render: (val?: string) => val || '—'
    },
    {
      title: t('defaultTargetingDetails'),
      dataIndex: 'defaultTargetingDetails',
      key: 'defaultTargetingDetails',
      ellipsis: { showTitle: true },
      render: (val?: string) => val || '—'
    },
    {
      title: t('status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (val: boolean) => (val ? t('active') : t('inactive'))
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEditModal(record)}>
            {t('edit')}
          </Button>
          <Button
            type="link"
            size="small"
            danger
            style={{ color: DELETE_ACTION_COLOR }}
            loading={isDeleting}
            onClick={() => handleDelete(record)}
          >
            {t('delete')}
          </Button>
        </Space>
      )
    }
  ];

  const channels = data?.channels ?? [];

  return (
    <>
      <Typography.Text strong style={{ display: 'block', marginBottom: 16, marginTop: 16, fontSize: 14 }}>
        {t('traderControlsChannels')}
      </Typography.Text>
      <Row style={{ marginBottom: 16 }} gutter={8}>
        <Button type="primary" onClick={openAddModal}>
          {t('addChannel')}
        </Button>
        <Button loading={isSeeding} onClick={handleSeedFromRateCards}>
          {t('seedChannelsFromRateCards')}
        </Button>
      </Row>
      <Table<AdminChannel>
        columns={columns}
        dataSource={channels.map((c) => ({ ...c, key: c._id }))}
        loading={isLoading}
        pagination={false}
        locale={{ emptyText: t('noData') }}
      />
      <Modal
        title={editingChannel ? t('editChannel') : t('addChannel')}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={closeModal}
        confirmLoading={isCreating || isUpdating}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="channel"
            label={t('channel')}
            rules={[{ required: true, message: t('requiredFieldMessage') }]}
          >
            <Input placeholder={t('channel')} />
          </Form.Item>
          {editingChannel && (
            <Form.Item name="isActive" label={t('status')} valuePropName="checked">
              <Switch checkedChildren={t('active')} unCheckedChildren={t('inactive')} />
            </Form.Item>
          )}
          <Divider orientation="left" plain style={{ margin: '8px 0 4px' }}>
            {t('lineItemDefaults')}
          </Divider>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
            {t('lineItemDefaultsHint')}
          </Typography.Paragraph>
          <Form.Item name="defaultTargetingDetails" label={t('defaultTargetingDetails')}>
            <Input.TextArea
              showCount
              maxLength={TARGETING_DETAILS_MAX_LENGTH}
              autoSize={{ minRows: 3, maxRows: 6 }}
              placeholder={t('defaultTargetingDetails')}
            />
          </Form.Item>
          <Form.Item name="defaultDsp" label={t('defaultDsp')}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              loading={areDspsLoading}
              placeholder={t('defaultDsp')}
              options={dspOptionsData?.dsps?.map((d) => ({ value: d.dspName, label: d.dspName })) ?? []}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TraderControlsChannels;
